/**
 * Cart checkpoint celebration effects (Sevune / Shrine Pro).
 *
 * Watches the cart drawer's checkpoint bar(s), single progress bar,
 * and free-gift unlock block for progress reaching 100% and fires a
 * canvas confetti/fireworks effect the moment a checkpoint is crossed.
 *
 * - "shipping" tier checkpoints (goal_1 / single progress bar) -> confetti shower
 * - "gift" tier checkpoints (goal_2 / goal_3 / gift block)     -> firework bursts
 *
 * No third-party libraries; self-contained canvas animation.
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  if (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  var DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

  var PALETTE = {
    shipping: ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#B983FF', '#FF6FB5'],
    gift: ['#FFD700', '#FF4D4D', '#FFFFFF', '#FFB800', '#FF8C42']
  };

  var canvas = null;
  var ctx = null;
  var particles = [];
  var rafId = null;

  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100vw;height:100vh;' +
      'pointer-events:none;z-index:2147483647;';
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
  }

  function resizeCanvas() {
    if (!canvas) return;
    canvas.width = window.innerWidth * DPR;
    canvas.height = window.innerHeight * DPR;
  }

  function startLoop() {
    if (rafId) return;
    rafId = requestAnimationFrame(tick);
  }

  function tick() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    var gravity = 0.06 * DPR;
    var alive = [];

    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.life++;

      if (p.type === 'confetti') {
        p.vy += gravity * 0.35;
        p.x += p.vx + Math.sin(p.life * p.drift) * DPR;
        p.y += p.vy;
        p.rot += p.vrot;
        if (p.life < p.maxLife && p.y < canvas.height + 40 * DPR) {
          drawConfetti(p);
          alive.push(p);
        }
      } else {
        p.vy += gravity;
        p.vx *= 0.98;
        p.vy *= 0.98;
        p.x += p.vx;
        p.y += p.vy;
        if (p.life < p.maxLife) {
          drawFirework(p);
          alive.push(p);
        }
      }
    }

    particles = alive;

    if (particles.length > 0) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  function drawConfetti(p) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.globalAlpha = Math.max(0, 1 - p.life / p.maxLife);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
    ctx.restore();
  }

  function drawFirework(p) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, 1 - p.life / p.maxLife);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function confettiShower() {
    ensureCanvas();
    var count = 90;
    for (var i = 0; i < count; i++) {
      particles.push({
        type: 'confetti',
        x: Math.random() * canvas.width,
        y: -20 * DPR - Math.random() * 220 * DPR,
        vx: (Math.random() - 0.5) * 2 * DPR,
        vy: (2 + Math.random() * 3) * DPR,
        size: (6 + Math.random() * 6) * DPR,
        color: PALETTE.shipping[(Math.random() * PALETTE.shipping.length) | 0],
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.3,
        life: 0,
        maxLife: 260 + Math.random() * 80,
        drift: Math.random() * 0.02 + 0.01
      });
    }
    startLoop();
  }

  function fireworkBurst(cx, cy) {
    var count = 55;
    for (var i = 0; i < count; i++) {
      var angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
      var speed = (3 + Math.random() * 4) * DPR;
      particles.push({
        type: 'firework',
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: (3 + Math.random() * 3) * DPR,
        color: PALETTE.gift[(Math.random() * PALETTE.gift.length) | 0],
        life: 0,
        maxLife: 55 + Math.random() * 25
      });
    }
  }

  function fireworksShow() {
    ensureCanvas();
    var bursts = 3;
    for (var b = 0; b < bursts; b++) {
      (function (delay) {
        setTimeout(function () {
          var cx = window.innerWidth * (0.25 + Math.random() * 0.5) * DPR;
          var cy = window.innerHeight * (0.2 + Math.random() * 0.35) * DPR;
          fireworkBurst(cx, cy);
          startLoop();
        }, delay);
      })(b * 260);
    }
  }

  // --- checkpoint watching -------------------------------------------

  var state = new Map();

  function checkTransition(key, reached, tier) {
    var previous = state.has(key) ? state.get(key) : null;
    state.set(key, reached);
    // First time we see this key: record the baseline, never fire —
    // we only want to celebrate a checkpoint crossed *live*, not one
    // that was already unlocked when the page loaded.
    if (previous === null) return;
    if (reached && !previous) {
      if (tier === 'gift') {
        fireworksShow();
      } else {
        confettiShower();
      }
    }
  }

  function scan(root) {
    root.querySelectorAll('[data-checkpoint-id]').forEach(function (el) {
      var key = el.getAttribute('data-checkpoint-id');
      var tier = el.getAttribute('data-checkpoint-tier') || 'shipping';
      var reachedAttr = el.hasAttribute('data-selected')
        ? el.getAttribute('data-selected')
        : el.getAttribute('data-reached');
      checkTransition(key, reachedAttr === 'true', tier);
    });
  }

  function init() {
    var root = document.getElementById('CartDrawer') || document.body;

    scan(root); // baseline pass, no effects fired

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        scan(root);
      }, 120);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-reached', 'data-selected']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
